/**
 * Error code translations from error codes to Hebrew messages
 * Error codes are defined in the backend (functions/errorCodes.js)
 */

// Error code to Hebrew message mapping
const errorCodeTranslations: Record<string, string> = {
  // Authentication errors
  'UNAUTHORIZED_NO_TOKEN': 'לא מורשה: לא סופק אסימון אימות',
  'UNAUTHORIZED_INVALID_TOKEN': 'לא מורשה: אסימון אימות לא תקין',
  'UNAUTHORIZED_ADMIN_REQUIRED': 'לא מורשה: נדרשות הרשאות מנהל',
  'USER_NOT_AUTHENTICATED': 'המשתמש לא מחובר',
  
  // Booking errors
  'BOOKING_CONFLICT': 'עמדה זו כבר הוזמנה בתאריכים אלו',
  'BOOKING_CREATE_FAILED': 'שגיאה ביצירת הזמנה',
  'BOOKING_NOT_FOUND': 'הזמנה לא נמצאה',
  'BOOKING_RETRIEVE_FAILED': 'שגיאה בקבלת פרטי ההזמנה שנוצרה',
  'BOOKING_GROUP_ALREADY_APPROVED': 'לא ניתן להוסיף הזמנות לקבוצה שכבר אושרה',
  
  // Lock code errors
  'LOCK_CODE_CREATE_FAILED': 'שגיאה ביצירת קוד מנעול',
  'LOCK_CODE_PENDING': 'הקוד ישלח ללקוח במייל נפרד בהקדם האפשרי',
  
  // Validation errors
  'VALIDATION_MISSING_FIELDS': 'חסרים שדות חובה: יש למלא מזהה עמדה, תאריך התחלה, תאריך סיום ומספר טלפון',
  'VALIDATION_INVALID_DATE_FORMAT': 'פורמט תאריך לא תקין. התאריכים חייבים להיות בפורמט ISO תקין',
  'VALIDATION_INVALID_DATE_RANGE': 'תאריך ההתחלה חייב להיות לפני תאריך הסיום',
  'VALIDATION_INVALID_FROM_DATE': 'פורמט תאריך התחלה לא תקין',
  'VALIDATION_INVALID_TO_DATE': 'פורמט תאריך סיום לא תקין',
  
  // Generic errors
  'UNKNOWN_ERROR': 'שגיאה לא ידועה',
  'INTERNAL_SERVER_ERROR': 'שגיאת שרת פנימית',
};

/**
 * Translates an error code to Hebrew message
 * @param errorCode - The error code
 * @returns The translated error message in Hebrew, or default message if code not found
 */
export function translateErrorCode(errorCode: string | null | undefined): string {
  if (!errorCode) {
    return errorCodeTranslations['UNKNOWN_ERROR'];
  }
  
  return errorCodeTranslations[errorCode] || errorCodeTranslations['UNKNOWN_ERROR'];
}

/**
 * Translates error from API response
 * API responses should have format: { error: "message", code: "ERROR_CODE" }
 * @param apiError - The API error response object or Error object
 * @returns The translated error message in Hebrew
 */
export function translateApiError(apiError: any): string {
  if (!apiError) {
    return translateErrorCode('UNKNOWN_ERROR');
  }
  
  // Handle Error object with code property (preserved from API response)
  if (apiError instanceof Error) {
    const errorCode = (apiError as any).code;
    if (errorCode) {
      return translateErrorCode(errorCode);
    }
    // If Error object has no code, it's likely a frontend error - return default
    console.warn('Error object without code property:', apiError.message);
    return translateErrorCode('UNKNOWN_ERROR');
  }
  
  // Handle API response object: { error: "...", code: "..." }
  if (typeof apiError === 'object') {
    const errorCode = apiError.code || apiError.errorCode;
    if (errorCode) {
      return translateErrorCode(errorCode);
    }
    // Log warning if we have an object but no code
    console.warn('API error object without code property:', apiError);
    return translateErrorCode('UNKNOWN_ERROR');
  }
  
  // Handle string (shouldn't happen with new system, but handle gracefully)
  if (typeof apiError === 'string') {
    console.warn('Received string error without code:', apiError);
    return translateErrorCode('UNKNOWN_ERROR');
  }
  
  return translateErrorCode('UNKNOWN_ERROR');
}
